/**
 * useGenerationTimeout Hook
 *
 * Manages timeout state for image generation flows.
 * Provides a 120-second default timeout to prevent stuck generation states,
 * with callbacks for timeout events.
 */

import { useState, useCallback, useEffect, useRef } from 'react';

/**
 * Options for the useGenerationTimeout hook
 */
export interface UseGenerationTimeoutOptions {
  /** Timeout duration in milliseconds (default: 120000ms / 2 minutes) */
  timeoutMs?: number;
  /** Callback fired when timeout occurs */
  onTimeout?: () => void;
}

/**
 * Return value for the useGenerationTimeout hook
 */
export interface UseGenerationTimeoutReturn {
  /** Whether the timeout has been reached */
  isTimedOut: boolean;
  /** Start the timeout timer */
  startTimeout: () => void;
  /** Reset and clear the timeout */
  resetTimeout: () => void;
}

/** Default timeout duration: 120 seconds */
const DEFAULT_TIMEOUT_MS = 120000;

/**
 * Hook to manage generation timeout state.
 *
 * Provides timeout tracking for long-running operations like image generation,
 * automatically resetting after the timeout duration to prevent stuck states.
 *
 * @param options - Configuration options
 * @returns Timeout state and control functions
 *
 * @example
 * const { isTimedOut, startTimeout, resetTimeout } = useGenerationTimeout({
 *   timeoutMs: 120000,
 *   onTimeout: () => setError('Generation timed out'),
 * });
 */
export function useGenerationTimeout(options: UseGenerationTimeoutOptions = {}): UseGenerationTimeoutReturn {
  const { timeoutMs = DEFAULT_TIMEOUT_MS, onTimeout } = options;

  const [isTimedOut, setIsTimedOut] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const resetTimeout = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    setIsTimedOut(false);
  }, []);

  const startTimeout = useCallback(() => {
    // Clear any existing timeout first
    resetTimeout();

    timeoutRef.current = setTimeout(() => {
      setIsTimedOut(true);
      onTimeout?.();
    }, timeoutMs);
  }, [timeoutMs, onTimeout, resetTimeout]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return {
    isTimedOut,
    startTimeout,
    resetTimeout,
  };
}

export default useGenerationTimeout;
